import { useEffect, useRef, useState } from 'react'
import { blocks, type Blocks } from '@w/content-blocks'
import { Button } from '@w/ui/components/button'
import { Input } from '@w/ui/components/input'
import { useTranslation } from '../i18n'

type Match = { blockId: string; text: string; start: number; length: number }

/** A query snapshot of this note's current document; never a workspace search or authored mutation. Opened by Cmd/Ctrl+F or the page menu's Find in note. */
export function PageFind({ readBlocks, onReveal, open, onOpenChange }: {
  readBlocks: () => Blocks; onReveal: (blockId: string) => void
  open: boolean; onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('page')
  const [query, setQuery] = useState('')
  const input = useRef<HTMLInputElement | null>(null)
  const panel = useRef<HTMLDivElement | null>(null)
  const root = useRef<HTMLDivElement | null>(null)

  const pattern = new RegExp(RegExp.escape(query), 'iu')
  const matches: Match[] = open && query.trim().length > 0 ? readBlocks().flatMap(block => {
    if (block.type === 'pageMeta' || block.type === 'pageTitle' || block.type === 'comments') return []
    const text = blocks.serialize([block], { plainText: true, trim: false })
    const match = pattern.exec(text)
    return match ? [{ blockId: block.id, text, start: match.index, length: match[0].length }] : []
  }) : []
  function show() {
    onOpenChange(true)
    input.current?.focus()
  }
  function close() {
    onOpenChange(false)
    root.current?.closest('[data-testid=flashcards-authoring-surface]')?.querySelector<HTMLElement>('[data-testid=content-blocks-editor-root]')?.focus()
  }
  useEffect(() => {
    if (open) input.current?.focus()
  }, [open])
  useEffect(() => {
    const owner = root.current?.closest('[data-testid=flashcards-authoring-surface]')
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target
      if (!(target instanceof Element) || event.isComposing) return
      // Notion: Cmd/Ctrl+F searches the open page from the note or the page body; another text field keeps its own Find.
      const fromNote = owner?.contains(target) === true && target.closest('[data-testid=content-blocks-editor-root], [data-testid=flashcards-page-header]') !== null
      const fromBody = target === document.body || target === document.documentElement
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'f' && !event.altKey && !event.shiftKey && (fromNote || fromBody)) {
        event.preventDefault()
        event.stopPropagation()
        show()
      } else if (event.key === 'Escape' && open && panel.current?.contains(target)) {
        event.preventDefault()
        event.stopPropagation()
        close()
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => { document.removeEventListener('keydown', onKeyDown, true) }
  })
  return <div ref={root} className="contents">
    {open ? <div ref={panel} className="border-separator bg-surface-0 absolute inset-x-0 top-full flex flex-col gap-2 border-b px-4 py-2 shadow-sm" role="search" aria-label={t('findInNote')}>
      <div className="flex items-center gap-2">
        <Input ref={input} className="min-w-0 flex-1" aria-label={t('findInNote')} data-testid="flashcards-find-note-query" value={query} onChange={event => { setQuery(event.target.value) }} />
        <Button data-testid="flashcards-find-note-close" size="sm" variant="text" semanticRole="secondary" onClick={close}>{t('closeFind')}</Button>
      </div>
      <p role="status" className="text-on-surface-secondary text-sm">{query.trim().length > 0 ? matches.length === 0 ? t('findNoMatches') : t('findBlockCount', { count: String(matches.length), unit: matches.length === 1 ? t('findBlock') : t('findBlocks') }) : t('findInNoteHint')}</p>
      {matches.length > 0 ? <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto">
        {matches.map((match, index) => <li key={match.blockId}>
          <button type="button" data-testid={`flashcards-find-note-result-${index}`} className="text-on-surface hover:bg-surface-1 w-full rounded px-2 py-1 text-left text-sm" onClick={() => { onReveal(match.blockId) }}>
            {match.text.slice(Math.max(0, match.start - 50), match.start)}<mark data-testid={`flashcards-find-note-highlight-${index}`}>{match.text.slice(match.start, match.start + match.length)}</mark>{match.text.slice(match.start + match.length, match.start + match.length + 100)}
          </button>
        </li>)}
      </ul> : null}
    </div> : null}
  </div>
}
